import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import Logo from '../Logo';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { logoutAsync } from '../../store/slices/authSlice';
import { API_ORIGIN } from '../../config/api';

/** Shared public-page shell styles (site-wide). */
export const publicPageSx = {
  width: '100%',
  maxWidth: '100%',
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
  overflowX: 'hidden',
  overflowY: 'auto',
  background: '#0B132B',
  color: '#F8FAFC',
  fontFamily: "'Inter', sans-serif",
} as const;

/** Fixed top bar height — keep spacer and AppBar in sync. */
export const PUBLIC_TOP_BAR_HEIGHT = { xs: 64, sm: 72 } as const;

const NAV_ITEMS = [
  { to: '/', label: 'Platform', key: 'home' },
  { to: '/map', label: 'Free map', key: 'map' },
  { to: '/pricing', label: 'Pricing', key: 'pricing' },
  { to: '/qgis-plugins', label: 'Plugins', key: 'plugins', badge: '2' },
  { to: '/developers/digipin', label: 'Developers', key: 'developers' },
] as const;

export type PublicSiteActive =
  | 'home'
  | 'map'
  | 'pricing'
  | 'plugins'
  | 'digipin'
  | 'cliptocad'
  | 'developers'
  | 'login'
  | 'issues'
  | 'profile'
  | 'account'
  | 'admin';

interface PublicSiteChromeProps {
  title?: string;
  subtitle?: string;
  active?: PublicSiteActive;
}

const navButtonSx = (isActive: boolean) =>
  ({
    minWidth: 0,
    px: { xs: 1, sm: 1.5, md: 1.75 },
    py: 0.85,
    borderRadius: 2,
    textTransform: 'none',
    fontSize: { xs: 12, sm: 13, md: 13.5 },
    fontWeight: isActive ? 700 : 500,
    color: isActive ? '#F8FAFC' : '#94A3B8',
    bgcolor: isActive ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
    border: isActive ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'all 0.2s ease',
    '&:hover': {
      color: '#F8FAFC',
      bgcolor: 'rgba(59, 130, 246, 0.1)',
      borderColor: 'rgba(59, 130, 246, 0.2)',
    },
  }) as const;

const ctaSx = {
  flexShrink: 0,
  px: { xs: 1.5, sm: 2.25 },
  py: 0.9,
  borderRadius: 2,
  textTransform: 'none',
  fontSize: { xs: 12, sm: 13 },
  fontWeight: 700,
  bgcolor: '#D97706',
  color: '#FFFFFF',
  boxShadow: '0 4px 14px rgba(217, 119, 6, 0.35)',
  whiteSpace: 'nowrap',
  '&:hover': {
    bgcolor: '#B45309',
    boxShadow: '0 6px 18px rgba(217, 119, 6, 0.5)',
  },
} as const;

/**
 * Canonical site-wide top bar for public and account pages.
 * Fixed position, auth-aware CTA, same nav on every page.
 */
export function PublicSiteChrome({ title = 'GeoVera', subtitle, active }: PublicSiteChromeProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, isLoading } = useAppSelector((state) => state.auth);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountAnchor, setAccountAnchor] = useState<null | HTMLElement>(null);

  const closeMobile = () => setMobileOpen(false);
  const closeAccount = () => setAccountAnchor(null);

  const go = (to: string) => {
    closeMobile();
    closeAccount();
    navigate(to);
  };

  const handleLogout = async () => {
    closeAccount();
    closeMobile();
    try {
      await dispatch(logoutAsync(API_ORIGIN)).unwrap();
    } catch {
      /* clear local auth regardless */
    }
    navigate('/login');
  };

  const showSignedIn = Boolean(isAuthenticated && user);
  const initial = (user?.username || user?.email || 'U').charAt(0).toUpperCase();

  const isNavActive = (key: string) => {
    if (key === 'plugins') {
      return (
        active === 'plugins' ||
        active === 'digipin' ||
        active === 'cliptocad' ||
        active === 'issues'
      );
    }
    return active === key;
  };

  const desktopNav = (item: (typeof NAV_ITEMS)[number]) => {
    const isActive = isNavActive(item.key);
    return (
      <Button key={item.key} component={RouterLink} to={item.to} sx={navButtonSx(isActive)}>
        {item.label}
        {'badge' in item && item.badge ? (
          <Chip
            label={item.badge}
            size="small"
            sx={{
              height: 16,
              fontSize: 9,
              fontWeight: 800,
              bgcolor: 'rgba(217, 119, 6, 0.2)',
              color: '#F59E0B',
              border: '1px solid rgba(217, 119, 6, 0.4)',
              px: 0.2,
            }}
          />
        ) : null}
      </Button>
    );
  };

  const primaryCta = showSignedIn ? (
    <Button
      component={RouterLink}
      to="/dashboard"
      variant="contained"
      size="small"
      startIcon={<DashboardOutlinedIcon sx={{ fontSize: '16px !important' }} />}
      sx={ctaSx}
    >
      Workspace
    </Button>
  ) : (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
      <Button
        href="mailto:support@geovera.in?subject=GeoVera%20Enterprise%20inquiry"
        size="small"
        sx={{
          display: { xs: 'none', lg: 'inline-flex' },
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 13,
          color: '#94A3B8',
          px: 1.25,
          '&:hover': { color: '#F8FAFC', bgcolor: 'rgba(59, 130, 246, 0.08)' },
        }}
      >
        Contact sales
      </Button>
      <Button
        component={RouterLink}
        to="/login"
        variant="contained"
        size="small"
        endIcon={<ArrowOutwardIcon sx={{ fontSize: '14px !important' }} />}
        sx={{
          ...ctaSx,
          ...(active === 'login'
            ? {
                bgcolor: 'rgba(217, 119, 6, 0.25)',
                color: '#FCD34D',
                boxShadow: 'none',
                border: '1px solid rgba(217, 119, 6, 0.45)',
                '&:hover': {
                  bgcolor: 'rgba(217, 119, 6, 0.35)',
                  boxShadow: 'none',
                },
              }
            : {}),
        }}
      >
        Sign In
      </Button>
    </Stack>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          width: '100%',
          maxWidth: '100%',
          bgcolor: 'rgba(11, 19, 43, 0.92)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: '1px solid rgba(219, 234, 254, 0.12)',
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: PUBLIC_TOP_BAR_HEIGHT.xs, sm: PUBLIC_TOP_BAR_HEIGHT.sm },
            height: { xs: PUBLIC_TOP_BAR_HEIGHT.xs, sm: PUBLIC_TOP_BAR_HEIGHT.sm },
            px: { xs: 1.5, sm: 2.5, md: 3.5, lg: 5 },
            gap: { xs: 0.75, sm: 1.25 },
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          {/* Mobile menu toggle */}
          <IconButton
            edge="start"
            aria-label="Open navigation menu"
            onClick={() => setMobileOpen(true)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              color: '#E2E8F0',
              flexShrink: 0,
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            component={RouterLink}
            to="/"
            aria-label={`${title} home`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: '0 1 auto',
              minWidth: 0,
              maxWidth: { xs: 150, sm: 240, md: 280 },
              mr: { xs: 0.5, md: 1 },
              color: 'inherit',
              textDecoration: 'none',
              overflow: 'hidden',
            }}
          >
            <Logo size={30} subtitle={subtitle} />
          </Box>

          {/* Desktop / tablet primary nav — same items on every page */}
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              flex: '1 1 auto',
              minWidth: 0,
              ml: 1,
            }}
          >
            {NAV_ITEMS.map(desktopNav)}
          </Stack>

          <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 auto' }, minWidth: 0 }} />

          <Stack
            direction="row"
            spacing={{ xs: 0.75, sm: 1.25 }}
            sx={{ alignItems: 'center', flexShrink: 0 }}
          >
            {primaryCta}

            {showSignedIn && (
              <>
                <Typography
                  sx={{
                    display: { xs: 'none', lg: 'block' },
                    color: '#94A3B8',
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: 12,
                    maxWidth: 180,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={user?.email}
                >
                  {user?.email}
                </Typography>
                <IconButton
                  aria-label="Account menu"
                  aria-haspopup="true"
                  aria-expanded={Boolean(accountAnchor)}
                  onClick={(e) => setAccountAnchor(e.currentTarget)}
                  sx={{
                    p: 0.3,
                    border: '1px solid rgba(59, 130, 246, 0.35)',
                    bgcolor: 'rgba(30, 58, 138, 0.35)',
                    '&:hover': { bgcolor: 'rgba(30, 58, 138, 0.55)' },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 30,
                      height: 30,
                      bgcolor: '#3B82F6',
                      color: '#F8FAFC',
                      fontSize: 13,
                      fontWeight: 800,
                    }}
                  >
                    {initial}
                  </Avatar>
                </IconButton>
                <Menu
                  anchorEl={accountAnchor}
                  open={Boolean(accountAnchor)}
                  onClose={closeAccount}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        minWidth: 220,
                        bgcolor: '#0F172A',
                        color: '#F8FAFC',
                        border: '1px solid rgba(219, 234, 254, 0.14)',
                        borderRadius: 2,
                        boxShadow: '0 16px 40px rgba(0,0,0,0.45)',
                        '& .MuiMenuItem-root': {
                          fontSize: 13.5,
                          py: 1.15,
                          gap: 1.25,
                          '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.12)' },
                        },
                      },
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.25 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{user?.username}</Typography>
                    <Typography sx={{ color: '#94A3B8', fontSize: 12, mt: 0.25 }}>
                      {user?.email}
                    </Typography>
                  </Box>
                  <Divider sx={{ borderColor: 'rgba(219, 234, 254, 0.1)' }} />
                  <MenuItem onClick={() => go('/account')}>
                    <PersonOutlinedIcon sx={{ fontSize: 18, color: '#60A5FA' }} />
                    Account
                  </MenuItem>
                  <MenuItem onClick={() => go('/dashboard')}>
                    <DashboardOutlinedIcon sx={{ fontSize: 18, color: '#60A5FA' }} />
                    Workspace
                  </MenuItem>
                  <MenuItem onClick={() => go('/profile')}>
                    <PersonOutlinedIcon sx={{ fontSize: 18, color: '#60A5FA' }} />
                    Profile
                  </MenuItem>
                  <MenuItem onClick={() => go('/developers/digipin')}>
                    <VpnKeyOutlinedIcon sx={{ fontSize: 18, color: '#60A5FA' }} />
                    DIGIPIN API
                  </MenuItem>
                  {user?.role === 'admin' && (
                    <MenuItem onClick={() => go('/admin')}>
                      <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 18, color: '#60A5FA' }} />
                      Admin hub
                    </MenuItem>
                  )}
                  <Divider sx={{ borderColor: 'rgba(219, 234, 254, 0.1)' }} />
                  <MenuItem onClick={handleLogout} disabled={isLoading}>
                    <LogoutIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
                    {isLoading ? 'Signing out…' : 'Sign out'}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Spacer so fixed bar never covers page content */}
      <Toolbar
        disableGutters
        aria-hidden
        sx={{
          minHeight: { xs: PUBLIC_TOP_BAR_HEIGHT.xs, sm: PUBLIC_TOP_BAR_HEIGHT.sm },
          height: { xs: PUBLIC_TOP_BAR_HEIGHT.xs, sm: PUBLIC_TOP_BAR_HEIGHT.sm },
          flexShrink: 0,
        }}
      />

      {/* Mobile navigation drawer — full nav on every page */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={closeMobile}
        slotProps={{
          paper: {
            sx: {
              width: 'min(300px, 88vw)',
              bgcolor: '#0B132B',
              color: '#F8FAFC',
              borderRight: '1px solid rgba(219, 234, 254, 0.12)',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(219, 234, 254, 0.1)',
          }}
        >
          <Logo size={28} subtitle={subtitle || 'WebGIS Platform'} />
          <IconButton
            aria-label="Close navigation menu"
            onClick={closeMobile}
            sx={{ color: '#94A3B8' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ px: 1, py: 1.5 }}>
          {NAV_ITEMS.map((item) => {
            const isActive = isNavActive(item.key);
            return (
              <ListItemButton
                key={item.key}
                component={RouterLink}
                to={item.to}
                onClick={closeMobile}
                selected={isActive}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(59, 130, 246, 0.15)',
                    '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.2)' },
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      sx: {
                        fontWeight: isActive ? 700 : 500,
                        fontSize: 14,
                      },
                    },
                  }}
                />
                {'badge' in item && item.badge ? (
                  <Chip
                    label={item.badge}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 800,
                      bgcolor: 'rgba(217, 119, 6, 0.2)',
                      color: '#F59E0B',
                    }}
                  />
                ) : null}
              </ListItemButton>
            );
          })}
        </List>

        <Divider sx={{ borderColor: 'rgba(219, 234, 254, 0.1)' }} />

        <Box sx={{ p: 2 }}>
          {showSignedIn ? (
            <Stack spacing={1}>
              <Typography sx={{ color: '#94A3B8', fontSize: 12 }}>{user?.email}</Typography>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DashboardOutlinedIcon />}
                onClick={() => go('/dashboard')}
                sx={ctaSx}
              >
                Open Workspace
              </Button>
              <Button
                fullWidth
                onClick={() => go('/profile')}
                sx={{ color: '#E2E8F0', textTransform: 'none' }}
              >
                Profile
              </Button>
              <Button
                fullWidth
                onClick={handleLogout}
                disabled={isLoading}
                sx={{ color: '#F59E0B', textTransform: 'none' }}
              >
                {isLoading ? 'Signing out…' : 'Sign out'}
              </Button>
            </Stack>
          ) : (
            <Button
              fullWidth
              variant="contained"
              component={RouterLink}
              to="/login"
              onClick={closeMobile}
              endIcon={<ArrowOutwardIcon />}
              sx={ctaSx}
            >
              Sign In
            </Button>
          )}
        </Box>
      </Drawer>
    </>
  );
}

export default PublicSiteChrome;
